import React, { useState, useEffect } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useCreateCategoryMutation, useUpdateCategoryMutation } from '../../../Services/PathwayAdminAPI';
import '../../../Styles/RoleManagement.css';
import '../../../Styles/CategoryForm.css';

const CategoryForm = ({ show, onHide, category = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order_index: 0,
  });
  const [errors, setErrors] = useState({});

  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  const isEditMode = !!category;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (!show) {
      setFormData({
        name: '',
        description: '',
        order_index: 0,
      });
      setErrors({});
      return;
    }

    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        order_index: category.order_index || 0,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        order_index: 0,
      });
    }
    setErrors({});
  }, [category, show]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Category name must not exceed 255 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must not exceed 1000 characters';
    }

    if (formData.order_index < 0) {
      newErrors.order_index = 'Order index must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' ? parseInt(value) || 0 : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await updateCategory({
          id: category.id,
          ...formData,
        }).unwrap();
      } else {
        response = await createCategory(formData).unwrap();
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.message || `Category ${isEditMode ? 'updated' : 'created'} successfully`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#FD9C00',
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
      onHide();
    } catch (error) {
      console.error('Error saving category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} category`,
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Body>
        <h5>{isEditMode ? 'Edit Category' : 'Add New Category'}</h5>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Category Name <span className="red-star">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  className={errors.name ? 'is-invalid' : ''}
                  maxLength={255}
                />
                {errors.name && <p className="error-text">{errors.name}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter category description (optional)"
                  rows={4}
                  className={errors.description ? 'is-invalid' : ''}
                  maxLength={1000}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #CFD6E1',
                    fontFamily: 'Open Sans',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
                {errors.description && <p className="error-text">{errors.description}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Order Index <span className="red-star">*</span>
                </label>
                <input
                  type="number"
                  name="order_index"
                  value={formData.order_index}
                  onChange={handleChange}
                  placeholder="Enter order index"
                  min={0}
                  className={errors.order_index ? 'is-invalid' : ''}
                />
                {errors.order_index && <p className="error-text">{errors.order_index}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Lower numbers appear first in the list
                </small>
              </div>
            </Col>
          </Row>
          <Row>
            <div className="btn-action">
              <button
                type="button"
                className="btn-outline-black"
                onClick={onHide}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={isLoading ? 'btn-primary-disabled' : 'btn-primary'}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CategoryForm;
